import { TutorialStorage } from "@/progress/TutorialStorage";

const STORAGE_KEY = "snake-arena-onboarding-v1";

export interface OnboardingState {
  schemaVersion: 1;
  /** The one-time EXPLORE THE WORLDS promotion shown on a player's first eligible Daily/Beat My Score result screen. Endless no longer reads or writes this flag - its own worlds-discovery CTA is now a persistent, every-result-screen part of `EndlessResultScene`, not a one-time promotion (see that Scene's own doc). */
  worldsDiscoveryShown: boolean;
  /**
   * New player goal clarity: the one-time, non-blocking "GROW AS LONG AS
   * YOU CAN" gameplay hint shown the moment movement begins on a player's
   * first-ever Endless run - see `GameScene`'s `shouldShowEndlessGoalHint`.
   * Never shown for Campaign/Daily.
   */
  endlessGoalHintShown: boolean;
}

/** The on-disk shape - `endlessGoalHintShown` is optional here only: absent on any record saved before this field existed, normalized to `false` by `readRaw()` below. `OnboardingState` itself (what every other function in this module reads/returns) always has the field present. */
interface StoredOnboardingState {
  schemaVersion: 1;
  worldsDiscoveryShown: boolean;
  endlessGoalHintShown?: boolean;
}

function isValid(value: unknown): value is StoredOnboardingState {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<StoredOnboardingState>;
  return candidate.schemaVersion === 1 && typeof candidate.worldsDiscoveryShown === "boolean";
}

/**
 * Conservative one-time migration default for a player with no
 * `onboarding-v1` record yet. `TutorialStorage.hasSeenIntro()` (CHOOSE
 * YOUR BITE completion) is the single reliable "has this player ever
 * played a real run before" signal: `GameScene` shows that intro
 * unconditionally on a player's very first run, in every mode (Campaign,
 * Daily, Endless) - see `GameScene.create()`'s `isIntroActive` field -
 * so anyone who has already dismissed it has necessarily played before
 * this feature shipped. Such a player is treated as having already seen
 * worlds discovery - an existing player must never receive that
 * promotion after the fact. A genuinely new visitor (a brand-new Beat My
 * Score visitor included, who never touches this evidence either) gets
 * `false`, so they still receive the one-time worlds-discovery promotion
 * later.
 */
function legacyDefault(): OnboardingState {
  const seen = TutorialStorage.hasSeenIntro();
  return { schemaVersion: 1, worldsDiscoveryShown: seen, endlessGoalHintShown: seen };
}

function readRaw(): OnboardingState | null {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!isValid(parsed)) return null;
    // A record saved before endlessGoalHintShown existed has no opinion on
    // it - normalize to `false` (not yet shown) rather than treating the
    // whole record as invalid, so an existing player's real
    // worldsDiscoveryShown history is never discarded just because this
    // field is new.
    return { schemaVersion: 1, worldsDiscoveryShown: parsed.worldsDiscoveryShown, endlessGoalHintShown: parsed.endlessGoalHintShown ?? false };
  } catch {
    return null;
  }
}

function save(state: OnboardingState): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage failures - onboarding state simply won't persist across a reload.
  }
}

/**
 * Onboarding state for Instant Play & Onboarding V1: the one-time
 * "discover the worlds" promotion. Its own namespaced key
 * (`snake-arena-onboarding-v1`), entirely separate from `TutorialStorage`
 * (CHOOSE YOUR BITE), `ProgressStorage` (Campaign), `EndlessStorage`,
 * `OnlineFeaturesStorage`, and every other existing store - clearing this
 * can never affect any of them, and vice versa.
 */
export const OnboardingStorage = {
  /**
   * Computes and persists the legacy-migration default exactly once - a
   * no-op on every call after the first (whether that first call created
   * a legacy-migrated record for a returning player or a genuinely-new
   * `{false}` record). Must be called once, synchronously, at app boot
   * (see `main.ts`) - strictly before any scene can change
   * `TutorialStorage`'s own flag this session, so the legacy snapshot
   * `legacyDefault()` reads is always accurate: `true` only for a player
   * who already had it set from a PRIOR session, `false` for a genuinely
   * new visitor even if they complete CHOOSE YOUR BITE moments later in
   * THIS session. Deliberately never re-derives this after the fact (see
   * `get()`'s doc) - a new player who completes CHOOSE YOUR BITE during
   * their first run must still see worlds discovery afterward.
   */
  ensureInitialized(): void {
    if (readRaw() !== null) return;
    save(legacyDefault());
  },

  /**
   * Pure read. Falls back to `legacyDefault()` (computed fresh, not
   * persisted) only if `ensureInitialized()` was never called first - a
   * safety net for isolated callers/tests, never relied upon in the real
   * app, where `main.ts` always calls `ensureInitialized()` at boot.
   */
  get(): OnboardingState {
    return readRaw() ?? legacyDefault();
  },

  hasShownWorldsDiscovery(): boolean {
    return this.get().worldsDiscoveryShown;
  },

  setWorldsDiscoveryShown(): void {
    const state = this.get();
    if (state.worldsDiscoveryShown) return;
    save({ ...state, worldsDiscoveryShown: true });
  },

  hasShownEndlessGoalHint(): boolean {
    return this.get().endlessGoalHintShown;
  },

  setEndlessGoalHintShown(): void {
    const state = this.get();
    if (state.endlessGoalHintShown) return;
    save({ ...state, endlessGoalHintShown: true });
  },
};
